/** @format */

import {Widget, WidgetStat} from 'components/PropertyWidget/model';

import {ValueBox} from 'components/PropertyWidget/style';

import WidgetFormattedNumber from 'components/PropertyWidget/components/WidgetFormattedNumber';

import {CommonChartData} from 'components/charts/multiline-chart/model/charts';

import {sumSeries, sumSeriesToDate} from 'features/mediaplan-progress/helpers/sumSeries';
import {diff} from 'features/mediaplan-progress/helpers/diff';
import {ratioPercent} from 'features/mediaplan-progress/helpers/ratioPercent';

import {safeDivide} from 'helpers/numberHelpers';

import getTokenWithSpace from '../../helpers/getTokenWithSpace';
import {ComplexValue} from '../../models/common';
import {
  factSeriesFromChartData,
  lastFactKey,
  planSeriesFromChartData,
} from '../progress-adapter/seriesFromChartData';

export interface GetStatsTypeAOptions {
  data: CommonChartData[];
  day: number;
  month: string;
  to: string;
  widget: Widget<ComplexValue>;
}

const getStatsTypeA = ({day, to, month, data, widget}: GetStatsTypeAOptions): WidgetStat[] => {
  const {token: tokenRaw, roundNumberBy, needSpaceBeforeToken, isPositiveRed} = widget;
  const token = getTokenWithSpace({token: tokenRaw, needSpaceBeforeToken});

  const planSeries = planSeriesFromChartData(data);
  const factSeries = factSeriesFromChartData(data);

  const plan = sumSeries(planSeries);
  const fact = sumSeries(factSeries);
  const averageInDay = safeDivide(fact, Object.keys(factSeries).length);

  const planAtTodayHeading = `План ${to} ${day} ${month}`;
  const planAtToday = sumSeriesToDate(planSeries, lastFactKey(data) ?? '');

  const diffToday = diff({plan: planAtToday, fact});
  const diffPercent = ratioPercent(diffToday, planAtToday);

  const isOverspending = diffToday >= 0;
  const diffHeading = isOverspending ? 'Перерасход' : 'Недорасход';

  const diffFuture = diff({plan, fact});
  const diffPercentFuture = ratioPercent(diffFuture, plan);

  const factAtTodayHeading = `Факт ${to} ${day} ${month}`;

  return [
    {
      heading: (
        <>
          План
          <br />
          недоступен
        </>
      ),
      showConditions: ['unreachable'],
    },
    {
      heading: 'План',
      ValueComponent: (
        <ValueBox>
          <WidgetFormattedNumber number={plan} roundNumberBy={roundNumberBy} postfix={token} />
        </ValueBox>
      ),
      showConditions: ['common'],
    },
    {
      heading: 'Факт',
      ValueComponent: (
        <ValueBox>
          <WidgetFormattedNumber number={fact} roundNumberBy={roundNumberBy} postfix={token} />
        </ValueBox>
      ),
      showConditions: ['common'],
    },
    {
      heading: planAtTodayHeading,
      ValueComponent: (
        <ValueBox>
          <WidgetFormattedNumber
            number={planAtToday}
            roundNumberBy={roundNumberBy}
            postfix={token}
          />
        </ValueBox>
      ),
      showConditions: ['future'],
    },
    {
      heading: factAtTodayHeading,
      ValueComponent: (
        <ValueBox>
          <WidgetFormattedNumber number={fact} roundNumberBy={roundNumberBy} postfix={token} />
        </ValueBox>
      ),
      showConditions: ['future'],
    },
    {
      heading: diffHeading,
      ValueComponent: (
        <ValueBox>
          <WidgetFormattedNumber
            number={diffToday}
            roundNumberBy={roundNumberBy}
            postfix={token}
            isPositiveRed={isPositiveRed}
            colorize
          />
          <WidgetFormattedNumber number={diffPercent} roundNumberBy={1} postfix='%' isGray />
        </ValueBox>
      ),
      showConditions: ['future'],
    },
    {
      heading: diffHeading,
      ValueComponent: (
        <ValueBox>
          <WidgetFormattedNumber
            number={diffFuture}
            roundNumberBy={roundNumberBy}
            postfix={token}
            isPositiveRed={isPositiveRed}
            colorize
          />
          <WidgetFormattedNumber number={diffPercentFuture} roundNumberBy={1} postfix='%' isGray />
        </ValueBox>
      ),
      showConditions: ['common'],
    },
    {
      heading: 'Ср. в день',
      ValueComponent: (
        <ValueBox>
          <WidgetFormattedNumber
            number={averageInDay}
            roundNumberBy={roundNumberBy}
            postfix={token}
          />
        </ValueBox>
      ),
      showConditions: ['unreachable'],
    },
    {
      heading: 'За период',
      ValueComponent: (
        <ValueBox>
          <WidgetFormattedNumber number={fact} roundNumberBy={roundNumberBy} postfix={token} />
        </ValueBox>
      ),
      showConditions: ['unreachable'],
    },
  ];
};

export default getStatsTypeA;
